import { Box, Button, TextField } from '@mui/material'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import type { PaginationState } from '@tanstack/table-core'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useMemo, useState } from 'react'
import { CreateEntityButton } from '@/components/CreateEntityButton'
import { DeleteConfirm } from '@/components/DeleteConfirm'
import { EntityTable } from '@/components/EntityTable'
import { LoadingComponent } from '@/components/LoadingComponent'
import {
	getEntityFormConfig,
	getEntityListQueryOptions,
	getEntityMutationEndpoint,
	getEntityMutationVariables,
	loadEntityListRoute,
	useEntityMutation,
} from '@/entity-config-runtime'
import {
	type EntityTableConfig,
	getEntityListTableConfig,
	getEntityTableColumnKeys,
	getEntityTableDefaultColumnVisibility,
} from '@/entity-config-runtime/entity-columns'
import type { EntityTableColumnConfig } from '@/entity-config-runtime/entity-table-config'
// import removed: entityCreateMap does not exist
import { useTableColumnConfig } from '@/hooks/use-table-column-config'
import { useAppAbility } from '@/integrations/casl/ability'
import { canReadEntityOrTab } from '@/integrations/casl/route-ability'
import * as messages from '@/integrations/paraglide/messages/_index.js'
import {
	general_cancel,
	general_delete,
	general_delete_confirm_prompt,
	general_delete_confirm_title,
	general_delete_relations_blocked_prefix,
	general_delete_relations_blocked_suffix,
} from '@/integrations/paraglide/messages/_index.js'
import {
	addFilters,
	addPagination,
	EntitysListSearchSchema,
} from '@/lib/entity-search'

export const Route = createFileRoute('/_authenticated/$entity/')({
	validateSearch: EntitysListSearchSchema,
	component: EntityListRoute,
	beforeLoad: ({ params, context }) => {
		const entity = String(params.entity ?? '')
		if (entity !== 'settings' && !canReadEntityOrTab(context.ability, entity)) {
			throw notFound({})
		}
	},
	loaderDeps({ search }) {
		const {
			sortColumn,
			sortDirection,

			skip,
			take,
		} = search

		return {
			...addPagination({ skip, take }),
			skip,
			take,
			filters: addFilters(search),
			sortColumn,
			sortDirection,
		}
	},
	async loader({
		params,
		context: { queryClient },
		deps: { filters, ...deps },
	}) {
		return loadEntityListRoute({
			params,
			search: { ...filters, ...deps },
			context: { queryClient },
		})
	},
	pendingComponent: LoadingComponent,
})

type MessageFn = () => string
const messageMap = messages as unknown as Record<string, MessageFn>
const relationLabelAliases: Record<string, string[]> = {
	contactCodeTrees: ['tab_contactCodeTree'],
	customPages: ['entity_custompage'],
	skillGroups: ['entity_skillgroup'],
	users: ['entity_user'],
}

function readMessage(key: string): string | undefined {
	const fn = messageMap[key]
	if (typeof fn === 'function') {
		return fn()
	}

	return undefined
}

function keyify(value: string): string {
	return value
		.replaceAll(/([a-z])([A-Z])/g, '$1_$2')
		.replaceAll(/[.\-\s/]+/g, '_')
		.replaceAll(/__+/g, '_')
		.replaceAll(/(?:^_+|_+$)/g, '')
		.toLowerCase()
}

function getRelationLabel(relationKey: string): string {
	const normalized = keyify(relationKey)
	const candidates = [
		...(relationLabelAliases[relationKey] ?? []),
		`entity_${relationKey}`,
		`entity_${normalized}`,
		`tab_${relationKey}`,
		`tab_${normalized}`,
		relationKey,
		normalized,
	]

	for (const candidate of candidates) {
		const localized = readMessage(candidate)
		if (localized) {
			return localized
		}
	}

	return relationKey
}

function getRelationCountColumns(config?: EntityTableConfig): Array<{
	key: string
	relationKey: string
}> {
	if (!config) return []

	return config.columns.flatMap((column: EntityTableColumnConfig) => {
		if (typeof column === 'string' || !column.relationCount) {
			return []
		}

		return [{ key: column.key, relationKey: column.relationCount.relationKey }]
	})
}

function EntityListRoute() {
	const ability = useAppAbility()
	const navigate = Route.useNavigate()
	const { entity } = Route.useParams()
	const entityKey = entity ?? ''
	const [isBooleanCellUpdateLoading, setIsBooleanCellUpdateLoading] =
		useState(false)
	const [rowDeleteState, setRowDeleteState] = useState<{
		open: boolean
		id: string
		name: string
		confirmationName: string
	}>({
		open: false,
		id: '',
		name: '',
		confirmationName: '',
	})
	const { filters, ...loaderDeps } = Route.useLoaderDeps()
	const query = useQuery({
		...getEntityListQueryOptions({
			params: { entity: entityKey },
			search: { ...filters, ...loaderDeps },
		}),
		placeholderData: keepPreviousData,
	})

	const data = query.data?.data || []
	const rowCount = query.data?.total || 0

	const configKey = entityKey

	const tableConfig = getEntityListTableConfig(configKey)
	const defaultColumns = getEntityTableColumnKeys(tableConfig)
	const defaultColumnVisibility =
		getEntityTableDefaultColumnVisibility(tableConfig)
	// Ensure all data rows have id: string (fallback to empty string if undefined)
	const safeData = Array.isArray(data)
		? data.map((row) => {
				const rowObject =
					row && typeof row === 'object' && !Array.isArray(row)
						? (row as Record<string, unknown>)
						: {}

				return {
					...rowObject,
					id: rowObject.id as string,
				}
			})
		: []
	const {
		columnVisibility,
		onColumnVisibilityChange,
		columnOrder,
		onColumnOrderChange,
	} = useTableColumnConfig(
		configKey,
		safeData,
		defaultColumns,
		defaultColumnVisibility,
	)
	// Keep filter keys stable even when filtered result is empty.
	const filterKeys = Array.from(
		new Set([
			...defaultColumns,
			...Object.keys(filters ?? {}),
			...(safeData.length > 0 ? Object.keys(safeData[0]) : []),
		]),
	).filter((key) => key !== 'id')

	const canCreate =
		!!getEntityMutationEndpoint({
			entity: entityKey,
			action: 'create',
		}) && !!getEntityFormConfig({ entity: entityKey, mode: 'create' })
	const updateEndpoint = getEntityMutationEndpoint({
		entity: entityKey,
		action: 'update',
	})
	const deleteEndpoint = getEntityMutationEndpoint({
		entity: entityKey,
		action: 'delete',
	})
	const canUpdate = Boolean(
		updateEndpoint &&
			(ability.can('modify', entityKey) || ability.can('write', entityKey)),
	)
	const canDelete = Boolean(
		deleteEndpoint &&
			(ability.can('modify', entityKey) || ability.can('write', entityKey)),
	)
	const updateMutation = useEntityMutation({
		entity: entityKey,
		action: 'update',
	})
	const deleteMutation = useEntityMutation({
		entity: entityKey,
		action: 'delete',
	})
	const relationCountColumns = useMemo(
		() => getRelationCountColumns(tableConfig),
		[tableConfig],
	)

	const tableEntityConfig = useMemo<EntityTableConfig | undefined>(() => {
		if (!tableConfig) return undefined
		if (!canDelete || !deleteEndpoint) return tableConfig

		return {
			...tableConfig,
			actions: [
				...(tableConfig.actions ?? []),
				{
					type: 'button',
					id: 'delete',
					label: 'general_delete',
					icon: 'Trash2',
					style: 'iconButton',
					onClick: (row: unknown) => {
						if (!row || typeof row !== 'object' || Array.isArray(row)) return
						const rowObject = row as Record<string, unknown>
						const blockingRelations = relationCountColumns.filter(({ key }) => {
							const value = rowObject[key]
							return typeof value === 'number'
								? value > 0
								: typeof value === 'string' && Number(value) > 0
						})

						if (blockingRelations.length > 0) {
							const relationLabels = blockingRelations
								.map(({ relationKey }) => getRelationLabel(relationKey))
								.join(', ')
							enqueueSnackbar(
								`${general_delete_relations_blocked_prefix()}${relationLabels}${general_delete_relations_blocked_suffix()}`,
								{ variant: 'warning' },
							)
							return
						}

						const rowId =
							typeof rowObject.id === 'string' ||
							typeof rowObject.id === 'number'
								? String(rowObject.id)
								: ''
						const rowName =
							typeof rowObject.name === 'string' ? rowObject.name : ''

						setRowDeleteState({
							open: true,
							id: rowId,
							name: rowName,
							confirmationName: '',
						})
					},
				},
			],
		}
	}, [canDelete, deleteEndpoint, relationCountColumns, tableConfig])

	// Pagination handler
	const onPaginationChange = useCallback(
		(next: PaginationState) => {
			navigate({
				search: (prev: typeof loaderDeps) => ({
					...prev,
					skip: next.pageIndex * next.pageSize,
					take: next.pageSize,
				}),
			})
		},
		[navigate],
	)

	// Sorting handler
	const onSortingChange = useCallback(
		(column: string, direction: number) => {
			navigate({
				search: (prev: typeof loaderDeps) => ({
					...prev,
					sortColumn: column,
					sortDirection: direction,
				}),
			})
		},
		[navigate],
	)

	// Filter handler: updates a single filter field
	const onFilterChange = useCallback(
		(key: string, value: string) => {
			navigate({
				search: (prev: typeof loaderDeps) => ({
					...prev,
					[key]: value === '' ? undefined : value, // Remove filter from search if value is empty
				}),
			})
		},
		[navigate],
	)

	const onBooleanCellChange = useCallback(
		(row: { id: string }, key: string, value: boolean) => {
			if (!canUpdate || !updateEndpoint || !row.id) return

			void (async () => {
				setIsBooleanCellUpdateLoading(true)
				try {
					await updateMutation.mutateAsync(
						getEntityMutationVariables({
							endpoint: updateEndpoint,
							entityId: row.id,
							data: { ...row, [key]: value },
						}),
					)
					enqueueSnackbar('Änderung gespeichert.', { variant: 'success' })
				} catch (error: unknown) {
					const message =
						error instanceof Error
							? error.message
							: 'Änderung konnte nicht gespeichert werden.'
					enqueueSnackbar(message, { variant: 'error' })
				}

				await query.refetch()
				setIsBooleanCellUpdateLoading(false)
			})()
		},
		[canUpdate, query, updateEndpoint, updateMutation],
	)

	const handleDeleteRow = useCallback(async () => {
		if (!canDelete || !deleteEndpoint || !rowDeleteState.id) {
			setRowDeleteState((prev) => ({ ...prev, open: false }))
			return
		}

		try {
			await deleteMutation.mutateAsync(
				getEntityMutationVariables({
					endpoint: deleteEndpoint,
					entityId: rowDeleteState.id,
				}),
			)
			enqueueSnackbar('Eintrag gelöscht.', { variant: 'success' })
			await query.refetch()
		} catch (error: unknown) {
			const message =
				error instanceof Error ? error.message : 'Löschen fehlgeschlagen.'
			enqueueSnackbar(message, { variant: 'error' })
		} finally {
			setRowDeleteState({
				open: false,
				id: '',
				name: '',
				confirmationName: '',
			})
		}
	}, [canDelete, deleteEndpoint, deleteMutation, query, rowDeleteState.id])

	const isDeleteConfirmationValid =
		!rowDeleteState.name ||
		rowDeleteState.confirmationName.trim() === rowDeleteState.name

	return (
		<>
			{/* Create button above table */}
			{canCreate && (
				<CreateEntityButton sx={{ mt: -8.75 }} entity={entityKey} />
			)}

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					height: '100%',
					minHeight: 0,
					marginTop: canCreate ? 0 : -1.75,
				}}
			>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<EntityTable
						data={safeData}
						entityConfig={tableEntityConfig}
						isLoading={query.isLoading}
						isFetching={query.isFetching}
						isBooleanCellUpdateLoading={isBooleanCellUpdateLoading}
						sortColumn={loaderDeps.sortColumn}
						sortDirection={loaderDeps.sortDirection}
						onSortingChange={onSortingChange}
						filters={filters}
						filterKeys={filterKeys}
						onFilterChange={onFilterChange}
						pagination={loaderDeps.pagination}
						onPaginationChange={onPaginationChange}
						rowCount={rowCount}
						columnVisibility={columnVisibility}
						onColumnVisibilityChange={onColumnVisibilityChange}
						columnOrder={columnOrder}
						onColumnOrderChange={onColumnOrderChange}
						onBooleanCellChange={canUpdate ? onBooleanCellChange : undefined}
						entity={entityKey}
					/>
					<DeleteConfirm
						open={rowDeleteState.open}
						title={general_delete_confirm_title()}
						description={`${general_delete_confirm_prompt()}${
							rowDeleteState.name ? ` (${rowDeleteState.name})` : ''
						}`}
					>
						<TextField
							fullWidth
							size="small"
							label="Name"
							value={rowDeleteState.confirmationName}
							onChange={(event) =>
								setRowDeleteState((prev) => ({
									...prev,
									confirmationName: event.target.value,
								}))
							}
							sx={{ mb: 2 }}
						/>
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
							<Button
								variant="outlined"
								onClick={() =>
									setRowDeleteState({
										open: false,
										id: '',
										name: '',
										confirmationName: '',
									})
								}
							>
								{general_cancel()}
							</Button>
							<Button
								color="error"
								variant="contained"
								onClick={handleDeleteRow}
								disabled={!isDeleteConfirmationValid}
							>
								{general_delete()}
							</Button>
						</Box>
					</DeleteConfirm>
				</Box>
			</Box>
		</>
	)
}
